/**
 * Spots recovery phrases and private keys in text people type into the support chat (D79), so
 * they are never sent to staff (and staff never send one to a customer). Used by the apps before
 * sending and enforced by the API.
 *
 *  - "definite" findings are always blocked;
 *  - "ambiguous" findings look exactly like normal support data (a raw 64-hex private key looks
 *    like a Bitcoin/Tron transaction id; a Solana secret key looks like a Solana signature). The
 *    sender must confirm it is a transaction id before it is sent.
 *
 * It errs on the side of blocking. It never returns the matched text, only what kind it was.
 */
import { sha256 } from "@noble/hashes/sha2.js";
import { base58, createBase58check } from "@scure/base";
import { wordlist } from "@scure/bip39/wordlists/english.js";
export const SECRET_KIND_LABEL = {
    recovery_phrase: "a recovery phrase",
    wif_private_key: "a Bitcoin private key",
    extended_private_key: "an extended private key",
    keypair_array: "a wallet key file",
    pem_private_key: "a private key block",
    hex_key_or_txid: "a private key or a transaction ID",
    base58_key_or_signature: "a Solana private key or transaction signature",
};
/** A run this long of BIP39 words is treated as a recovery phrase (12 is the shortest phrase). */
export const PHRASE_RUN = 12;
const WORDS = new Set(wordlist);
const base58check = createBase58check(sha256);
/** Longest run of consecutive BIP39 words (numbers, punctuation and line breaks between them are ignored). */
export function longestPhraseRun(text) {
    // Letters only, so "1. abandon 2. ability" and "abandon,ability" count as consecutive words.
    const tokens = text.toLowerCase().match(/[a-z]+/g) ?? [];
    let best = 0;
    let run = 0;
    for (const t of tokens) {
        run = WORDS.has(t) ? run + 1 : 0;
        if (run > best)
            best = run;
    }
    return best;
}
function isWif(candidate) {
    try {
        const payload = base58check.decode(candidate);
        // version byte (0x80 mainnet / 0xef testnet) + 32-byte key (+ 0x01 when compressed)
        return ((payload[0] === 0x80 || payload[0] === 0xef) &&
            (payload.length === 33 || (payload.length === 34 && payload[33] === 0x01)));
    }
    catch {
        return false;
    }
}
function isBase58Of(candidate, bytes) {
    try {
        return base58.decode(candidate).length === bytes;
    }
    catch {
        return false;
    }
}
export function detectSecrets(text) {
    const found = new Map();
    const add = (kind, certainty) => {
        if (!found.has(kind))
            found.set(kind, { kind, certainty });
    };
    if (longestPhraseRun(text) >= PHRASE_RUN)
        add("recovery_phrase", "definite");
    if (/-----BEGIN [A-Z ]*PRIVATE KEY-----/.test(text))
        add("pem_private_key", "definite");
    // Extended private keys: xprv / tprv / yprv / zprv / uprv / vprv (+ capital variants), base58.
    if (/\b[xtyzuvYZUV]prv[1-9A-HJ-NP-Za-km-z]{100,112}\b/.test(text))
        add("extended_private_key", "definite");
    // A list of 64 numbers 0–255 (Solana keypair file / byte array).
    for (const m of text.matchAll(/\[\s*(\d{1,3}\s*,\s*){63,}\d{1,3}\s*\]/g)) {
        const nums = m[0].match(/\d+/g).map(Number);
        if (nums.length === 64 && nums.every((n) => n <= 255))
            add("keypair_array", "definite");
    }
    for (const m of text.matchAll(/[1-9A-HJ-NP-Za-km-z]{50,90}/g)) {
        const s = m[0];
        if ((s.length === 51 || s.length === 52) && isWif(s))
            add("wif_private_key", "definite");
        // 64 bytes in base58 = Solana secret key, but also the shape of a Solana signature.
        else if (s.length >= 86 && s.length <= 88 && isBase58Of(s, 64))
            add("base58_key_or_signature", "ambiguous");
    }
    // 64 hex characters NOT starting with 0x: a raw private key, or a Bitcoin/Tron transaction id.
    // (0x + 64 hex is how EVM transaction hashes are written; raw key exports have no 0x.)
    if (/(?<![0-9a-fA-Fx])[0-9a-fA-F]{64}(?![0-9a-fA-F])/.test(text))
        add("hex_key_or_txid", "ambiguous");
    return [...found.values()];
}
/** What the app does with a message: send it, ask "is it a transaction ID?", or refuse. */
export function secretVerdict(text, confirmedTxIds = false) {
    const findings = detectSecrets(text);
    if (findings.some((f) => f.certainty === "definite"))
        return "block";
    if (findings.length > 0 && !confirmedTxIds)
        return "confirm";
    return "ok";
}
//# sourceMappingURL=secret-detector.js.map